// @ts-nocheck
import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { JourneyType, JourneyStopType } from '../types/journey';

// Types for journey editing
export type EditMode = 'view' | 'edit';

type BaseChange = { sequence: number };

export type Change = 
  | (BaseChange & {
      type: 'add_stop';
      position: number;
      stopId: string;
      stop: Partial<JourneyStopType>;
      method: 'manual' | 'ai_assisted';
      instructions?: string;
    })
  | (BaseChange & {
      type: 'remove_stop';
      stopId: string;
      stopOrder?: number;
    })
  | (BaseChange & {
      type: 'replace_stop';
      stopId: string;
      stopOrder?: number;
      newStop: Partial<JourneyStopType>;
      method: 'manual' | 'ai_assisted';
      instructions?: string;
    })
  | (BaseChange & {
      type: 'update_leg_transport';
      legId: string;
      transportMode: string;
      targetStopOrder?: number;
    });

export interface JourneyEditState {
  mode: EditMode;
  originalJourney: JourneyType | null;
  currentJourney: JourneyType | null;
  pendingChanges: Change[];
  isSubmitting: boolean;
  awaitingApproval: boolean; // True when journey has been processed and is waiting for user approval
  processedJourney: JourneyType | null; // The processed journey from review endpoint
}

type JourneyEditAction =
  | { type: 'LOAD_JOURNEY'; payload: JourneyType }
  | { type: 'ENTER_EDIT_MODE' }
  | { type: 'EXIT_EDIT_MODE' }
  | { type: 'ADD_CHANGE'; payload: Change }
  | { type: 'DISCARD_CHANGES' }
  | { type: 'START_SUBMITTING' }
  | { type: 'FINISH_SUBMITTING' }
  | { type: 'UPDATE_CURRENT_JOURNEY'; payload: JourneyType }
  | { type: 'SET_AWAITING_APPROVAL'; payload: JourneyType } // Set processed journey and enter approval state
  | { type: 'CLEAR_APPROVAL_STATE' } // Clear approval state (after approval or discard)
  | { type: 'UPDATE_ORIGINAL_JOURNEY_ID'; payload: { journeyId: string; journey: JourneyType } }; // Update original journey with backend ID after auto-save

const initialState: JourneyEditState = {
  mode: 'view',
  originalJourney: null,
  currentJourney: null,
  pendingChanges: [],
  isSubmitting: false,
  awaitingApproval: false,
  processedJourney: null,
};

/**
 * Tracks how many times ensureStopIds has been called.
 * Used to detect if the function is being called in an infinite loop.
 */
let ensureStopIdsCallCount = 0;

/**
 * Cache to prevent redundant processing of the same journey object.
 * WeakMap allows garbage collection when journey objects are no longer referenced.
 * Key: Original journey object, Value: Journey with IDs ensured
 */
const ensureStopIdsCache = new WeakMap<JourneyType, JourneyType>();

/**
 * ensureStopIds
 * 
 * Ensures that every stop AND leg in a JourneyType object has a unique `id` property.
 * Generates new UUIDs where IDs are missing for stops, legs, and journey itself.
 * 
 * IMPORTANT: Returns the original object reference if all IDs are already present.
 * This is critical to prevent unnecessary object recreation which causes infinite render loops.
 * 
 * OPTIMIZATION: Uses WeakMap cache to avoid redundant processing when React Strict Mode
 * or reducer double-invocations call this function multiple times with the same input object.
 * 
 * - If the journey already has stops/legs with IDs in metadata, they are preserved.
 * - If any stop, leg, or journey is missing an ID, a UUID is generated and assigned.
 * - If the journey has no metadata, it is constructed from the stops with generated IDs.
 * 
 * @param {JourneyType | null} journey - The journey whose stop and leg IDs need to be ensured.
 * @returns {JourneyType | null} - The same journey, but with IDs ensured on stops, legs, and metadata.
 */
function ensureStopIds(journey: JourneyType | null): JourneyType | null {
  // Track function calls for debugging
  ensureStopIdsCallCount++;
  
  if (!journey) {
    console.log(`[ensureStopIds] Call #${ensureStopIdsCallCount}: No journey provided`);
    return journey;
  }
  
  // OPTIMIZATION: Check cache first to avoid redundant processing
  const cached = ensureStopIdsCache.get(journey);
  if (cached) {
    console.log(`[ensureStopIds] Call #${ensureStopIdsCallCount} ⚡ Cache HIT - returning cached result`);
    return cached;
  }

  // CRITICAL: Validate that journey has stops array
  if (!journey.stops || !Array.isArray(journey.stops)) {
    console.error(`[ensureStopIds] Call #${ensureStopIdsCallCount} ❌ CRITICAL: Journey has no stops array!`, {
      journey,
      hasStops: !!journey.stops,
      isArray: Array.isArray(journey.stops),
      journeyKeys: Object.keys(journey)
    });
    return journey; // Return as-is to prevent crash
  }

  const stopsWithIdsCount = journey.stops.filter(s => !!s.id).length;
  // Check both v2 format (legs at root) and v1 format (_metadata.journeyLegs)
  const rootLegs = (journey as any).legs;
  const legCount = Array.isArray(rootLegs) ? rootLegs.length : (journey._metadata?.journeyLegs?.length || 0);
  const legsWithIdsCount = Array.isArray(rootLegs) 
    ? rootLegs.filter((l: any) => !!l.id).length 
    : (journey._metadata?.journeyLegs?.filter((l: any) => !!l.id).length || 0);
  const allStopsHaveIds = journey.stops?.length === stopsWithIdsCount;
  const allLegsHaveIds = legCount === legsWithIdsCount;

  console.log(`[ensureStopIds] Call #${ensureStopIdsCallCount} 🔍 Input journey:`, {
    hasId: !!(journey as any).id,
    journeyId: (journey as any).id,
    hasMetadata: !!journey._metadata,
    hasRootLegs: Array.isArray(rootLegs),
    stopsCount: journey.stops?.length,
    stopsWithIdsCount,
    legsCount: legCount,
    legsWithIdsCount,
    allStopsHaveIds,
    allLegsHaveIds,
    willReturnOriginal: allStopsHaveIds && allLegsHaveIds
  });
  
  // Warn if called too many times (potential infinite loop)
  if (ensureStopIdsCallCount > 100 && ensureStopIdsCallCount % 10 === 0) {
    console.warn(`[ensureStopIds] ⚠️ High call count: ${ensureStopIdsCallCount} - potential infinite loop!`);
  }

  // CRITICAL: If all stops and legs already have IDs, return the original object!
  // This prevents unnecessary object recreation and infinite loops
  if (allStopsHaveIds && allLegsHaveIds && legCount > 0) {
    console.log(`[ensureStopIds] Call #${ensureStopIdsCallCount} ✅ All IDs already present - returning original object (same reference)`);
    return journey;
  }

  console.log(`[ensureStopIds] Call #${ensureStopIdsCallCount} 🔧 Need to generate IDs - creating new objects`);

  // Maps stop order to generated or existing stop ID
  const stopIdByOrder = new Map<number, string>();

  // Check if metadata needs cloning (any IDs missing in journeyStops or journeyLegs)
  let needsMetadataClone = false;
  if (journey._metadata) {
    // Check journeyStops for missing IDs and build stopIdByOrder map
    journey._metadata.journeyStops?.forEach(meta => {
          if (!meta.id) needsMetadataClone = true;
          const ensuredId = meta.id || uuidv4();
          stopIdByOrder.set(meta.stopOrder, ensuredId);
    });
    // Check journeyLegs for missing IDs
    journey._metadata.journeyLegs?.forEach((leg: any) => {
          if (!leg.id) needsMetadataClone = true;
    });
      }

  // Ensure each stop has an ID, correlating with metadata if available, otherwise generate a new one.
  let needsStopClone = false;
  const stopsWithIdsArray = journey.stops.map(stop => {
    if (!stop.id) needsStopClone = true;
    const ensuredId = stop.id || stopIdByOrder.get(stop.order) || uuidv4();
    stopIdByOrder.set(stop.order, ensuredId);
    return stop.id ? stop : { ...stop, id: ensuredId };
  });

  // V2 FORMAT: Ensure legs array has IDs if it exists at root level
  // If legs don't exist, create them from stop data (for legacy v1 data conversion)
  let legsArray: any[] = (journey as any).legs;
  let needsLegClone = false;
  
  if (Array.isArray(legsArray)) {
    // V2 format - ensure all legs have IDs
    legsArray = legsArray.map((leg: any) => {
      if (!leg.id) needsLegClone = true;
      return leg.id ? leg : { ...leg, id: uuidv4() };
    });
  } else {
    // No legs array - create from stop data (v1 to v2 conversion)
    needsLegClone = true;
    legsArray = stopsWithIdsArray.slice(1).map((stop: any, index: number) => ({
      id: uuidv4(),
      order: index + 1,
      fromStopId: stopsWithIdsArray[index].id!,
      toStopId: stop.id!,
      transportMode: stop.transport_mode_from_previous || stop.transportMode || 'auto',
      distanceKm: stop.distance_from_previous_km || stop.distanceKm || 0,
      durationHours: stop.transport_time_from_previous_hours || stop.durationHours || 0,
      costCents: Math.round((stop.transport_cost_eur || 0) * 100) || stop.costCents || 0,
      costCurrency: 'EUR',
      co2eKg: stop.transport_co2e_kg_from_previous || stop.co2eKg || 0,
      hasOutdatedValues: false,
    }));
  }
  
  // Create v2 metadata (only has flags, no duplicated data)
  const v2Metadata = {
    hasOutdatedValues: journey._metadata?.hasOutdatedValues ?? legsArray.some((l: any) => l.hasOutdatedValues) ?? false,
    schemaVersion: '2.0'
  };
  
  // If no changes were needed, return original object
  if (!needsStopClone && !needsLegClone && !needsMetadataClone) {
    console.log(`[ensureStopIds] Call #${ensureStopIdsCallCount} ✅ No changes needed - returning original object`);
    return journey;
  }
  
  // Build result with v2 format: legs at root level
  const result: any = {
    ...journey,
    stops: stopsWithIdsArray,
    legs: legsArray,
    _metadata: v2Metadata,
  };
  
  console.log(`[ensureStopIds] Call #${ensureStopIdsCallCount} ✅ Created v2 format (NEW OBJECT):`, {
    stopsCount: result.stops.length,
    legsCount: result.legs.length,
    allStopsHaveIds: result.stops.every((s: any) => !!s.id),
    allLegsHaveIds: result.legs.every((l: any) => !!l.id),
    isNewObject: true
  });
  
  // Cache the result to avoid redundant processing
  ensureStopIdsCache.set(journey, result);
  return result as JourneyType;
}

/**
 * Main reducer for journey edit state management.
 * Handles all state transitions for the editing workflow.
 */
function journeyEditReducer(state: JourneyEditState, action: JourneyEditAction): JourneyEditState {
  switch (action.type) {
    case 'LOAD_JOURNEY':
      const journeyWithIds = ensureStopIds(action.payload) as JourneyType;
      return {
        ...state,
        mode: 'view',
        originalJourney: journeyWithIds,
        currentJourney: journeyWithIds,
        pendingChanges: [],
        isSubmitting: false,
        awaitingApproval: false,
        processedJourney: null,
      };

    case 'ENTER_EDIT_MODE':
      return {
        ...state,
        mode: 'edit',
      };

    case 'EXIT_EDIT_MODE':
      return {
        ...state,
        mode: 'view',
        pendingChanges: [],
        isSubmitting: false,
      };

    case 'ADD_CHANGE':
      const newState = {
        ...state,
        mode: 'edit' as EditMode,
        pendingChanges: [...state.pendingChanges, action.payload],
      };

      // Apply the change to currentJourney
      if (state.currentJourney) {
        newState.currentJourney = applyChangeToJourney(state.currentJourney, action.payload);
      }

      return newState;

    case 'DISCARD_CHANGES':
      return {
        ...state,
        mode: 'view',
        currentJourney: state.originalJourney,
        pendingChanges: [],
        isSubmitting: false,
        awaitingApproval: false,
        processedJourney: null,
      };

    case 'START_SUBMITTING':
      return {
        ...state,
        isSubmitting: true,
      };

    case 'FINISH_SUBMITTING':
      return {
        ...state,
        isSubmitting: false,
      };

    case 'UPDATE_CURRENT_JOURNEY':
      const updatedJourney = ensureStopIds(action.payload) as JourneyType;
      return {
        ...state,
        currentJourney: updatedJourney,
      };

    case 'SET_AWAITING_APPROVAL':
      const processedJourney = ensureStopIds(action.payload) as JourneyType;
      return {
        ...state,
        awaitingApproval: true,
        processedJourney: processedJourney,
        currentJourney: processedJourney,
        pendingChanges: [], // Clear pending changes as they're now processed
        isSubmitting: false,
        mode: 'edit', // Keep in edit mode
      };

    case 'CLEAR_APPROVAL_STATE':
      return {
        ...state,
        awaitingApproval: false,
        processedJourney: null,
      };

    case 'UPDATE_ORIGINAL_JOURNEY_ID':
      // Update the original journey with the backend ID after auto-save
      // Crucially, this preserves pendingChanges and currentJourney
      const journeyWithBackendId = ensureStopIds(action.payload.journey) as JourneyType;
      return {
        ...state,
        originalJourney: journeyWithBackendId,
        // Keep everything else the same - don't touch pendingChanges or currentJourney
      };

    default:
      return state;
  }
}

// Helper function to apply changes to journey
function applyChangeToJourney(journey: JourneyType, change: Change): JourneyType {
  const sourceJourney = ensureStopIds(journey) as JourneyType;
  const newJourney: JourneyType = {
    ...sourceJourney,
    stops: sourceJourney.stops.map(stop => ({ ...stop })),
    _metadata: sourceJourney._metadata
      ? {
          ...sourceJourney._metadata,
          journeyStops: sourceJourney._metadata.journeyStops?.map(meta => ({ ...meta })) || [],
          journeyLegs: sourceJourney._metadata.journeyLegs?.map(leg => ({ ...leg })) || [],
        }
      : sourceJourney._metadata,
  };

  switch (change.type) {
    case 'add_stop': {
      const insertIndex = Math.max(0, Math.min(newJourney.stops.length, change.position - 1));
      const newStop: JourneyStopType = {
        id: change.stopId,
        order: change.position,
        location: change.stop.location || 'New Stop',
        location_country: change.stop.location_country || 'TBD',
        latitude: change.stop.latitude || 0,
        longitude: change.stop.longitude || 0,
        distance_from_previous_km: 0, // Will be calculated later
        transport_mode_from_previous: 'auto',
        transport_co2e_kg_from_previous: 0, // Will be calculated later
        transport_time_from_previous_hours: 0, // Will be calculated later
        transport_cost_eur: 0,
        things_to_do: change.stop.things_to_do || [],
      };

      const stopsWithNew = [...newJourney.stops];
      stopsWithNew.splice(insertIndex, 0, newStop);

      newJourney.stops = stopsWithNew.map((stop, index) => {
        const updatedStop = { ...stop, order: index + 1 };
        if (index === insertIndex + 1) {
          updatedStop.distance_from_previous_km = 0;
          updatedStop.transport_co2e_kg_from_previous = 0;
          updatedStop.transport_time_from_previous_hours = 0;
          updatedStop.transport_cost_eur = 0;
        }
        return updatedStop;
      });
      break;
    }

    case 'remove_stop': {
      let stopIndex = newJourney.stops.findIndex(stop => stop.id === change.stopId);
      if (stopIndex === -1 && change.stopOrder != null) {
        stopIndex = newJourney.stops.findIndex(stop => stop.order === change.stopOrder);
      }
      if (stopIndex === -1) break;

      const removedStop = newJourney.stops[stopIndex];
      const stopAfterRemoved = newJourney.stops[stopIndex + 1];
      const isFirstStop = stopIndex === 0;
      const isLastStop = stopIndex === newJourney.stops.length - 1;

      const filteredStops = newJourney.stops.filter((_, idx) => idx !== stopIndex);

      if (isLastStop) {
        newJourney.stops = filteredStops.map((stop, index) => ({ ...stop, order: index + 1 }));
      } else if (isFirstStop) {
        newJourney.stops = filteredStops.map((stop, index) => {
          if (index === 0) {
            return {
              ...stop,
              order: 1,
              distance_from_previous_km: 0,
              transport_mode_from_previous: '',
              transport_co2e_kg_from_previous: 0,
              transport_time_from_previous_hours: 0,
              transport_cost_eur: 0,
            };
          }
          return { ...stop, order: index + 1 };
        });
      } else {
        newJourney.stops = filteredStops.map((stop, index) => {
          if (stopAfterRemoved && stop.id === stopAfterRemoved.id) {
            return {
              ...stop,
              order: index + 1,
              transport_mode_from_previous: removedStop.transport_mode_from_previous,
              distance_from_previous_km: 0,
              transport_co2e_kg_from_previous: 0,
              transport_time_from_previous_hours: 0,
              transport_cost_eur: 0,
            };
          }
          return { ...stop, order: index + 1 };
        });
      }
      break;
    }

    case 'replace_stop': {
      let replaceIndex = newJourney.stops.findIndex(stop => stop.id === change.stopId);
      if (replaceIndex === -1 && change.stopOrder != null) {
        replaceIndex = newJourney.stops.findIndex(stop => stop.order === change.stopOrder);
      }
      if (replaceIndex === -1) break;

      const isReplacingFirst = replaceIndex === 0;
      const isReplacingLast = replaceIndex === newJourney.stops.length - 1;

      const filteredStopsForReplace = newJourney.stops.filter((_, idx) => idx !== replaceIndex);

      const replacementStop: JourneyStopType = {
        id: change.stopId,
        order: newJourney.stops[replaceIndex].order,
        location: change.newStop.location || 'New Stop',
        location_country: change.newStop.location_country || 'TBD',
        latitude: change.newStop.latitude || 0,
        longitude: change.newStop.longitude || 0,
        distance_from_previous_km: 0,
        transport_mode_from_previous: isReplacingFirst ? '' : 'auto',
        transport_co2e_kg_from_previous: 0,
        transport_time_from_previous_hours: 0,
        transport_cost_eur: 0,
        things_to_do: [],
      };

      filteredStopsForReplace.splice(replaceIndex, 0, replacementStop);

      newJourney.stops = filteredStopsForReplace.map((stop, index) => {
        if (index === replaceIndex) {
          return { ...stop, order: index + 1 };
        }
        if (index === replaceIndex + 1 && !isReplacingLast) {
          return {
            ...stop,
            order: index + 1,
            transport_mode_from_previous: 'auto',
            distance_from_previous_km: 0,
            transport_co2e_kg_from_previous: 0,
            transport_time_from_previous_hours: 0,
            transport_cost_eur: 0,
          };
        }
        return { ...stop, order: index + 1 };
      });
      break;
    }

    case 'update_leg_transport': {
      const targetOrder =
        change.targetStopOrder ??
        (() => {
          const leg = newJourney._metadata?.journeyLegs?.find(l => l.id === change.legId);
          if (!leg) return undefined;
          const targetStop = newJourney._metadata?.journeyStops?.find(s => s.id === leg.toStopId);
          return targetStop?.stopOrder;
        })();

      if (targetOrder == null) break;

      newJourney.stops = newJourney.stops.map(stop => {
        if (stop.order === targetOrder) {
          return {
            ...stop,
            transport_mode_from_previous: change.transportMode,
            distance_from_previous_km: 0,
            transport_co2e_kg_from_previous: 0,
            transport_time_from_previous_hours: 0,
            transport_cost_eur: 0,
          };
        }
        return stop;
      });
      break;
    }
  }

  return newJourney;
}

// Context
const JourneyEditContext = createContext<{
  state: JourneyEditState;
  dispatch: React.Dispatch<JourneyEditAction>;
} | null>(null);

// Provider component
export const JourneyEditProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(journeyEditReducer, initialState);

  return (
    <JourneyEditContext.Provider value={{ state, dispatch }}>
      {children}
    </JourneyEditContext.Provider>
  );
};

// Hook to use the context
export const useJourneyEdit = () => {
  const context = useContext(JourneyEditContext);
  if (!context) {
    throw new Error('useJourneyEdit must be used within a JourneyEditProvider');
  }
  return context;
};

// Helper hooks for common operations
export const useJourneyEditActions = () => {
  const { dispatch } = useJourneyEdit();

  return React.useMemo(() => ({
    loadJourney: (journey: JourneyType) => dispatch({ type: 'LOAD_JOURNEY', payload: journey }),
    enterEditMode: () => dispatch({ type: 'ENTER_EDIT_MODE' }),
    exitEditMode: () => dispatch({ type: 'EXIT_EDIT_MODE' }),
    addChange: (change: Change) => dispatch({ type: 'ADD_CHANGE', payload: change }),
    discardChanges: () => dispatch({ type: 'DISCARD_CHANGES' }),
    startSubmitting: () => dispatch({ type: 'START_SUBMITTING' }),
    finishSubmitting: () => dispatch({ type: 'FINISH_SUBMITTING' }),
    updateCurrentJourney: (journey: JourneyType) => dispatch({ type: 'UPDATE_CURRENT_JOURNEY', payload: journey }),
    setAwaitingApproval: (journey: JourneyType) => dispatch({ type: 'SET_AWAITING_APPROVAL', payload: journey }),
    clearApprovalState: () => dispatch({ type: 'CLEAR_APPROVAL_STATE' }),
    updateOriginalJourneyId: (journeyId: string, journey: JourneyType) => dispatch({ type: 'UPDATE_ORIGINAL_JOURNEY_ID', payload: { journeyId, journey } }),
  }), [dispatch]);
};
